import React, { useState, useMemo } from 'react';
import {
    Button,
    Card,
    Col,
    Divider,
    Form,
    Row,
    Select,
    Space,
    Tag,
    Typography,
    Table,
    Segmented,
    Statistic
} from 'antd';
import {
    SearchOutlined,
    InboxOutlined,
    UndoOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import PageTitle from '../../../components/common/PageTitle';
import {
    branchOfficeMaster,
    itemModelMaster,
    stockByBranch
} from '../../../utility/mockData';

const { Text, Title, Paragraph } = Typography;

const OtherBranchStock = () => {
    const config = {
        title: 'View Other Branch Stock',
        subtitle: 'Review item model and SKU availability across different branch inventories in real-time.',
    };
    // --- OTHER BRANCH STOCK STATES ---
    const [stockSearchMode, setStockSearchMode] = useState('branch'); // 'branch', 'sku', 'both'
    const [selectedStockBranch, setSelectedStockBranch] = useState(null);
    const [selectedStockSku, setSelectedStockSku] = useState(null);
    const [stockSkuSearchVal, setStockSkuSearchVal] = useState('');
    const [stockResults, setStockResults] = useState([]);
    const [hasSearchedStock, setHasSearchedStock] = useState(false);


    // --- OTHER BRANCH STOCK RENDER LOGIC ---
    const handleStockSearch = () => {
        let results = [...stockByBranch];

        if (stockSearchMode === 'branch') {
            if (selectedStockBranch) {
                results = results.filter(s => s.branchCode === selectedStockBranch);
            }
        } else if (stockSearchMode === 'sku') {
            if (selectedStockSku) {
                results = results.filter(s => s.sku === selectedStockSku);
            }
        } else if (stockSearchMode === 'both') {
            if (selectedStockBranch) {
                results = results.filter(s => s.branchCode === selectedStockBranch);
            }
            if (selectedStockSku) {
                results = results.filter(s => s.sku === selectedStockSku);
            }
        }

        setStockResults(results);
        setHasSearchedStock(true);
    };

    const handleStockReset = () => {
        setSelectedStockBranch(null);
        setSelectedStockSku(null);
        setStockSkuSearchVal('');
        setStockResults([]);
        setHasSearchedStock(false);
    };

    const stockSummaryMetrics = useMemo(() => {
        if (!hasSearchedStock) return null;
        const totalStock = stockResults.reduce((acc, curr) => acc + curr.quantity, 0);
        const uniqueSKUs = new Set(stockResults.map(s => s.sku)).size;
        const totalBranches = new Set(stockResults.map(s => s.branchCode)).size;
        const lowStockRecords = stockResults.filter(s => s.quantity < 10).length;

        return { totalStock, uniqueSKUs, totalBranches, lowStockRecords };
    }, [stockResults, hasSearchedStock]);



        return (
            <PageTitle title={config.title} subtitle={config.subtitle}>
                <Row gutter={[24, 24]}>
                    <Col xs={24} xl={8}>
                        <Card title={<Space><SearchOutlined /> Stock Search Panel</Space>} style={{ borderRadius: '16px' }}>
                            <Space direction="vertical" size={20} style={{ width: '100%' }}>
                                <div>
                                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Search Option Type</Text>
                                    <Segmented
                                        block
                                        value={stockSearchMode}
                                        onChange={(val) => {
                                            setStockSearchMode(val);
                                            handleStockReset();
                                        }}
                                        options={[
                                            { label: 'By Branch', value: 'branch' },
                                            { label: 'By SKU', value: 'sku' },
                                            { label: 'By Both', value: 'both' }
                                        ]}
                                    />
                                </div>

                                <Divider style={{ margin: '8px 0' }} />

                                <Form layout="vertical" onFinish={handleStockSearch}>
                                    {(stockSearchMode === 'branch' || stockSearchMode === 'both') && (
                                        <Form.Item label="Target Branch office" required>
                                            <Select
                                                placeholder="Select a branch to search"
                                                value={selectedStockBranch}
                                                onChange={setSelectedStockBranch}
                                                allowClear
                                                showSearch
                                                optionFilterProp="children"
                                            >
                                                {branchOfficeMaster.map(b => (
                                                    <Select.Option key={b.branchCode} value={b.branchCode}>
                                                        {b.branchName} ({b.branchCode})
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}

                                    {(stockSearchMode === 'sku' || stockSearchMode === 'both') && (
                                        <Form.Item label="Target Item Model (SKU)" required>
                                            <Select
                                                placeholder="Select SKU / Item Model"
                                                value={selectedStockSku}
                                                onChange={(val) => {
                                                    setSelectedStockSku(val);
                                                    setStockSkuSearchVal('');
                                                }}
                                                allowClear
                                                showSearch
                                                searchValue={stockSkuSearchVal}
                                                onSearch={setStockSkuSearchVal}
                                                optionFilterProp="children"
                                                notFoundContent={stockSkuSearchVal.length < 3 ? "Type at least 3 characters to search..." : undefined}
                                            >
                                                {stockSkuSearchVal.length >= 3 && itemModelMaster.map(s => (
                                                    <Select.Option key={s.sku} value={s.sku}>
                                                        {s.modelName} ({s.sku})
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}

                                    <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 12 }}>
                                        <Button icon={<UndoOutlined />} onClick={handleStockReset}>
                                            Reset
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            htmlType="submit"
                                            style={
                                                ((stockSearchMode === 'branch' && !selectedStockBranch) ||
                                                 (stockSearchMode === 'sku' && !selectedStockSku) ||
                                                 (stockSearchMode === 'both' && (!selectedStockBranch || !selectedStockSku)))
                                                 ? {}
                                                 : { background: '#175AA0', borderColor: '#175AA0' }
                                            }
                                            disabled={
                                                (stockSearchMode === 'branch' && !selectedStockBranch) ||
                                                (stockSearchMode === 'sku' && !selectedStockSku) ||
                                                (stockSearchMode === 'both' && (!selectedStockBranch || !selectedStockSku))
                                            }
                                        >
                                            Search Stock
                                        </Button>
                                    </Space>
                                </Form>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} xl={16}>
                        {!hasSearchedStock ? (
                            <Card style={{ borderRadius: '16px', textAlign: 'center', padding: '40px 20px' }}>
                                <InboxOutlined style={{ fontSize: '50px', color: '#bfbfbf', marginBottom: '16px' }} />
                                <Title level={4}>Inventory Query Matrix</Title>
                                <Paragraph type="secondary" style={{ maxWidth: '440px', margin: '0 auto' }}>
                                    Select an option type on the left, define your branch or item model filters, and hit "Search Stock" to visualize real-time inventory results.
                                </Paragraph>
                            </Card>
                        ) : (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {/* Metrics Cards */}
                                <Row gutter={[16, 16]}>
                                    <Col xs={12} sm={6}>
                                        <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: '12px' }}>
                                            <Statistic
                                                title="Total Stock Count"
                                                value={stockSummaryMetrics.totalStock}
                                                valueStyle={{ color: '#175AA0', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: '12px' }}>
                                            <Statistic
                                                title="Distinct SKUs"
                                                value={stockSummaryMetrics.uniqueSKUs}
                                                valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: '12px' }}>
                                            <Statistic
                                                title="Branches Found"
                                                value={stockSummaryMetrics.totalBranches}
                                                valueStyle={{ color: '#13c2c2', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: '12px' }}>
                                            <Statistic
                                                title="Low Stock Alerts"
                                                value={stockSummaryMetrics.lowStockRecords}
                                                valueStyle={{ color: stockSummaryMetrics.lowStockRecords > 0 ? '#f5222d' : '#8c8c8c', fontWeight: 'bold' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Results Table Card */}
                                <Card
                                    title={<Space><BarChartOutlined /> Stock Availability Report</Space>}
                                    style={{ borderRadius: '16px' }}
                                >
                                    <Table
                                        rowKey={(record) => `${record.branchCode}_${record.sku}`}
                                        dataSource={stockResults}
                                        bordered
                                        scroll={{ x: 'max-content' }}
                                        columns={[
                                            {
                                                title: 'Branch',
                                                dataIndex: 'branchName',
                                                key: 'branchName',
                                                width: 250,
                                                render: (text, record) => (
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong>{text}</Text>
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>{record.branchCode}</Text>
                                                    </Space>
                                                )
                                            },
                                            {
                                                title: 'Item Model SKU',
                                                dataIndex: 'modelName',
                                                key: 'modelName',
                                                width: 250,
                                                render: (text, record) => (
                                                    <Space direction="vertical" size={0}>
                                                        <Text strong style={{ color: '#175AA0' }}>{text}</Text>
                                                        <Text code style={{ fontSize: '11px' }}>{record.sku}</Text>
                                                    </Space>
                                                )
                                            },
                                            {
                                                title: 'Available Qty',
                                                dataIndex: 'quantity',
                                                key: 'quantity',
                                                align: 'center',
                                                width: 150,
                                                render: (qty) => {
                                                    let color = '#52c41a';
                                                    if (qty === 0) color = '#f5222d';
                                                    else if (qty < 10) color = '#fa8c16';

                                                    return (
                                                        <span style={{ fontSize: '16px', fontWeight: 'bold', color }}>
                                                            {qty}
                                                        </span>
                                                    );
                                                }
                                            },
                                            {
                                                title: 'Availability Status',
                                                dataIndex: 'quantity',
                                                key: 'status',
                                                width: 180,
                                                render: (qty) => {
                                                    if (qty === 0) return <Tag color="error">Out of Stock</Tag>;
                                                    if (qty < 10) return <Tag color="warning">Low Stock</Tag>;
                                                    return <Tag color="success">In Stock</Tag>;
                                                }
                                            }
                                        ]}
                                    />
                                </Card>
                            </Space>
                        )
                        }
                    </Col>
                </Row>
            </PageTitle>
        );
};

export default OtherBranchStock;
